import React, { useEffect } from "react";
import { Box, Typography, Grid } from "@mui/material";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  accent: '#0ea5e9',
};

export default function Help() {
  useEffect(() => {
    document.title = "Help | KlonIT Web Application";
  }, []);

  const contactItems = [
    {
      icon: <EmailOutlinedIcon sx={{ fontSize: '1.5rem' }} />,
      label: 'Email Support',
      value: 'help@klonit.net',
      description: 'Get help via email',
      color: '#3b82f6',
    },
    {
      icon: <PhoneOutlinedIcon sx={{ fontSize: '1.5rem' }} />,
      label: 'Phone Support',
      value: '001 123 456 789',
      description: 'Call us directly',
      color: '#10b981',
    },
    {
      icon: <MenuBookOutlinedIcon sx={{ fontSize: '1.5rem' }} />,
      label: 'Documentation',
      value: 'docs.klonit.ai',
      description: 'Browse our guides',
      color: '#8b5cf6',
      link: 'https://docs.klonit.ai',
    },
  ];

  return (
    <Box sx={{ fontFamily, maxWidth: '900px', width: '100%', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
          boxShadow: '0 8px 24px rgba(14,165,233,0.3)',
        }}>
          <HelpOutlineIcon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em' }}>
          Help Center
        </Typography>
        <Typography sx={{ fontFamily, fontSize: '0.875rem', color: colors.textTertiary, mt: 0.5 }}>
          Get the support you need
        </Typography>
      </Box>

      {/* Contact Cards */}
      <Grid container spacing={3}>
        {contactItems.map((item, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Box
              component={item.link ? 'a' : 'div'}
              href={item.link}
              target={item.link ? '_blank' : undefined}
              rel={item.link ? 'noopener noreferrer' : undefined}
              sx={{
                display: 'block',
                bgcolor: 'rgba(255,255,255,0.8)',
                backdropFilter: 'blur(12px)',
                borderRadius: '16px',
                p: 3,
                border: '1px solid rgba(0,0,0,0.06)',
                textAlign: 'center',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                cursor: item.link ? 'pointer' : 'default',
                '&:hover': {
                  transform: item.link ? 'translateY(-4px)' : 'none',
                  boxShadow: item.link ? '0 12px 32px rgba(0,0,0,0.1)' : 'none',
                },
              }}
            >
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: `${item.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                color: item.color,
              }}>
                {item.icon}
              </Box>
              <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary, mb: 0.5 }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 600, color: item.color, mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                {item.value}
                {item.link && <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />}
              </Typography>
              <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
                {item.description}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
